import React from "react";
import { buildSrcSet } from "../../utils/responsive-srcset";

export type ResponsiveImageProps = {
    /** Source image path (without width suffix) */
    src: string;
    /** Alt text for accessibility (required) */
    alt: string;
    /** Widths to generate srcset for */
    widths?: number[];
    /** Sizes attribute for responsive sizing */
    sizes?: string;
    /** Priority loading for LCP images (eager instead of lazy) */
    priority?: boolean;
    /** CSS classes for the wrapper div */
    className?: string;
    /** CSS classes for the img element itself */
    imgClassName?: string;
    /** CSS object-fit value (cover, contain, fill, none, scale-down) */
    objectFit?: "cover" | "contain" | "fill" | "none" | "scale-down";
    /** CSS object-position value (e.g., "center", "top", "bottom 10% left 20%") */
    objectPosition?: string;
    /** Image format (defaults to webp with jpg fallback) */
    format?: "webp" | "jpg" | "png";
};

/**
 * Responsive image component with automatic srcset generation
 *
 * Features:
 * - Automatic srcset for multiple device widths
 * - Native lazy loading (`loading="lazy"`), unless priority=true
 * - WebP format with fallback
 *
 * ## Warum natives Lazy-Loading und kein IntersectionObserver (8.8.2026)
 *
 * Bis heute startete ein `useEffect` einen `IntersectionObserver`, und das
 * `<img>` entstand erst bei Sichtkontakt; davor stand ein grauer
 * Platzhalter-`div`. Im SSG-Lauf (`vite-react-ssg` rendert in Node) laeuft kein
 * Observer — das Bild fehlte im ausgelieferten HTML, und nach der Hydration
 * erschien es ebenfalls nicht.
 *
 * Gemessen am gebauten Artefakt: **0** Bilder mit der Lazy-Signatur
 * (`loading="lazy"` + `decoding="async"`) im gesamten `dist`, gegenueber 54
 * `priority`-Bildern, die den Observer nie brauchten.
 *
 * `CustomerLogoCarousel.tsx` zeigt, warum eine Reparatur des Observers nicht
 * gereicht haette: Dessen Kacheln beziehen ihre Hoehe aus `imgClassName`, also
 * vom `<img>`. Kein Bild → keine Hoehe → Container 0 px → der Observer kann
 * nicht feuern → kein Bild. Eine selbstverstaerkende Schleife.
 *
 * `WorkshopMethodsSection` hatte den Fehler bereits erkannt und mit einem
 * handgebauten `<img srcSet loading="lazy">` umgangen — genau das tut diese
 * Komponente jetzt selbst.
 *
 * Der Blur-up-Platzhalter (`placeholder`-Prop) entfaellt: Kein einziger der 27
 * Aufrufe hat ihn je gesetzt. `loading="lazy"` ist seit Juni 2026 Baseline
 * Widely Available.
 *
 * Plan: `docs/plans/2026-08-08-responsive-image-ssg.md`
 *
 * @example
 * ```tsx
 * <ResponsiveImage
 *   src="/images/hero.jpg"
 *   alt="Hero image"
 *   widths={[640, 960, 1280, 1920]}
 *   sizes="(max-width: 768px) 100vw, 50vw"
 *   priority={true}
 * />
 * ```
 */
export const ResponsiveImage: React.FC<ResponsiveImageProps> = ({
    src,
    alt,
    widths = [320, 640, 960, 1280, 1920],
    sizes = "100vw",
    priority = false,
    className = "",
    imgClassName = "",
    objectFit = "cover",
    objectPosition = "center",
    format = "webp",
}) => {
    // Check if the image is an SVG (vector format that doesn't need responsive variants)
    const isSvg = src.toLowerCase().endsWith(".svg");

    // Nur Varianten benennen, die es wirklich gibt (11.8.2026). Vorher wurde
    // fuer jede angefragte Breite ein Kandidat behauptet — auf `/referenzen`
    // waren 66 von 109 davon 404, und `srcset` faellt bei 404 nicht auf `src`
    // zurueck. Ein leeres Ergebnis ist der sichere Fall: dann greift `src`.
    const srcSet = buildSrcSet(src, widths, format);
    const fallbackSrc = src; // Original image as fallback

    // Build image style
    const imgStyle: React.CSSProperties = {
        objectFit,
        objectPosition,
    };

    // Build default img classes if not overridden
    const defaultImgClasses = imgClassName || "w-full h-full block";

    // For SVG files, render directly without srcset (SVGs are vector and don't need variants)
    if (isSvg) {
        return (
            <div className={`relative ${className}`}>
                <img
                    src={src}
                    alt={alt}
                    loading={priority ? "eager" : "lazy"}
                    decoding={priority ? "sync" : "async"}
                    className={defaultImgClasses}
                    style={imgStyle}
                />
            </div>
        );
    }

    // Check if className includes positioning (absolute, relative, fixed, sticky)
    const hasPositioning = className && /\b(absolute|relative|fixed|sticky)\b/.test(className);
    const positionClass = hasPositioning ? "" : "relative";

    return (
        <div className={`${positionClass} overflow-hidden w-full h-full ${className}`}>
            {/* Das Bild steht immer im Markup — der Browser entscheidet ueber
                `loading`, wann es geladen wird. Kein Platzhalter-Zweig mehr:
                Genau der stand bisher dauerhaft im SSG-HTML, weil der Observer,
                der ihn haette ersetzen sollen, dort nie lief. */}
            <img
                src={fallbackSrc}
                srcSet={srcSet || undefined}
                sizes={srcSet ? sizes : undefined}
                alt={alt}
                loading={priority ? "eager" : "lazy"}
                decoding={priority ? "sync" : "async"}
                className={defaultImgClasses}
                style={imgStyle}
            />
        </div>
    );
};
